/** Route-driven import-run detail dialog — /import-runs/:id/:tab?
 *
 * Each tab is deep-linkable (`info` | `input` | `logs` | `reimport`); tab
 * switches navigate (so browser back/forward walks the tab history and the
 * Share button can copy an exact link), closing navigates back to the grid
 * with its query string intact. Reimport is manage-perm-gated — without the
 * permission the tab is hidden and a deep link falls back to Info.
 */
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import * as importRunsApi from '@/api/importRuns';
import { useAuth } from '@/auth/AuthProvider';
import { useSnackbar } from '@/components/Snackbar';
import { utcTitle, ValueActions } from '@/components/ValueActions/ValueActions';
import { TsValue } from '@/features/bgtasks/taskFormat';

import { ImportStateChip, importOriginLabel, importStatusLabel, shortId } from '../importRunFormat';
import { createdActions, originActions, statusActions, tenantActions } from '../importRunLinks';
import { AiDiagnosePanel } from '../diagnose/AiDiagnosePanel';
import { InfoTab } from './InfoTab';
import { InputTab } from './InputTab';
import { LogsTab } from './LogsTab';
import { ReimportTab } from './ReimportTab';

const TABS = [
  { id: 'info', label: 'Info' },
  { id: 'input', label: 'Input' },
  { id: 'logs', label: 'Logs' },
  { id: 'reimport', label: 'Reimport', perm: 'console_import_runs_manage' },
  { id: 'diagnose', label: 'Diagnose', perm: 'console_import_ai' },
] as const;

export interface ImportRunDetailDialogProps {
  runId: string;
  tab: string | undefined;
  onClose: () => void;
  onNavigateTab: (tab: string) => void;
  /** Route to another run's dialog (keeps the grid's query string). */
  detailPath: (id: string) => string;
}

export function ImportRunDetailDialog({
  runId,
  tab,
  onClose,
  onNavigateTab,
  detailPath,
}: ImportRunDetailDialogProps) {
  const auth = useAuth();
  const snackbar = useSnackbar();

  const visibleTabs = TABS.filter((t) => !('perm' in t) || auth.hasPerm(t.perm));
  const activeTab = visibleTabs.find((t) => t.id === tab)?.id ?? 'info';

  const detail = useQuery({
    queryKey: ['import-runs', 'detail', runId],
    queryFn: () => importRunsApi.retrieve(runId),
  });

  const copyLink = () => {
    void navigator.clipboard.writeText(
      `${window.location.origin}/import-runs/${runId}/${activeTab}`,
    );
    snackbar.show('Link copied to clipboard.', 'success');
  };

  const d = detail.data;
  // Every quick filter on this dialog stays inside the run's own tenant.
  const ctx = { tenantId: d?.top?.id ?? null };

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { height: '88vh' } }}
    >
      <Box sx={{ px: 3, pt: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="h6" component="h2" sx={{ fontFamily: 'monospace' }}>
              Import {shortId(runId)}…
            </Typography>
            {d && (
              <ValueActions chip actions={statusActions(importStatusLabel(d.state), ctx)}>
                <ImportStateChip state={d.state} />
              </ValueActions>
            )}
            <Tooltip title="Copy import ID">
              <IconButton size="small" onClick={() => {
                void navigator.clipboard.writeText(runId);
                snackbar.show('Import ID copied.', 'success');
              }} aria-label="Copy import ID">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy link to this tab">
              <IconButton size="small" onClick={copyLink} aria-label="Copy link to this tab">
                <ShareIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          {d && (
            <Typography variant="body2" color="text.secondary" noWrap>
              {d.top && (
                <>
                  <ValueActions actions={tenantActions(d.top)}>{d.top.name}</ValueActions>
                  {' · '}
                </>
              )}
              <ValueActions actions={originActions(importOriginLabel(d.origin), ctx)}>
                {importOriginLabel(d.origin)}
              </ValueActions>
              {' · '}
              <ValueActions actions={createdActions(d.created_ts, ctx)} title={utcTitle(d.created_ts)}>
                <TsValue value={d.created_ts} />
              </ValueActions>
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={activeTab} onChange={(_, v: string) => onNavigateTab(v)}>
          {visibleTabs.map((t) => (
            <Tab key={t.id} value={t.id} label={t.label} />
          ))}
        </Tabs>
      </Box>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 3 }}>
        {detail.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {detail.error != null && (
          <Alert severity="error">Couldn’t load import run {runId}.</Alert>
        )}
        {d && activeTab === 'info' && <InfoTab detail={d} detailPath={detailPath} />}
        {d && activeTab === 'input' && <InputTab runId={runId} />}
        {d && activeTab === 'logs' && <LogsTab runId={runId} />}
        {d && activeTab === 'reimport' && (
          <ReimportTab detail={d} detailPath={detailPath} />
        )}
        {d && activeTab === 'diagnose' && <AiDiagnosePanel runId={runId} />}
      </DialogContent>
    </Dialog>
  );
}
